/**
 * The Ledger Dialog. Import dialogs from here, never from `@chakra-ui/react`
 * (a Biome rule enforces it).
 *
 * The look is the `dialog` recipe (`src/theme/recipes/layers.ts`): sm 480 /
 * md 640 / lg 800, radius 6, shadow-3, an opaque surface, the scrim behind.
 * On top of Chakra this wrapper:
 *
 * - traps focus inside the dialog and returns it to the opener on close (a
 *   caller may still pass `trapFocus={false}`);
 * - never locks the page's scroll, so nothing behind the dialog shifts;
 * - `busy`: while the dialog saves, Esc, a press outside and the close button
 *   do nothing, and the content stays as it is (`ConfirmDialog` and
 *   `DestructiveDialog` also hold their Cancel);
 * - `Dialog.Body error`: an error inside the dialog is a banner at the top of
 *   its body, and the dialog stays open;
 * - wraps the content in an error boundary, so a render crash shows inline
 *   instead of closing the dialog;
 * - takes its stacking from the layer manager (`hooks/useOverlayZIndex.ts`).
 *
 * Spec: specs/design-system/ledger-layers.feature
 */
// eslint-disable-next-line no-restricted-imports
import { Alert, Dialog as ChakraDialog, Portal } from "@chakra-ui/react";
import * as React from "react";
import {
  escapeKeyDownThatMarksClose,
  ModalLayerContext,
  OverlayDepthContext,
  openerForReturnFocus,
  pinLayerZIndex,
  useModalLayer,
} from "~/hooks/useOverlayZIndex";
import { CloseButton } from "./close-button";
import { IsolatedErrorBoundary } from "./IsolatedErrorBoundary";

/** Whether the enclosing dialog is saving, read by its close button. */
const DialogBusyContext = React.createContext(false);

export const useDialogBusy = () => React.useContext(DialogBusyContext);

interface DialogContentProps extends ChakraDialog.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  /** Set to `false` for a dialog that should not dim the page. */
  backdrop?: boolean;
  /** Props merged onto the backdrop. Its colour is the scrim token. */
  backdropProps?: ChakraDialog.BackdropProps;
  /** Props passed to the positioner. */
  positionerProps?: ChakraDialog.PositionerProps;
  /**
   * Set to `false` to disable the inline error boundary that wraps
   * children. By default, a render-time crash inside a dialog body shows
   * an inline error panel — it does NOT close the dialog or take down the
   * page. Opt out only if you have a more specific outer boundary already.
   */
  withErrorBoundary?: boolean;
  /** Optional scope label shown by the error fallback. */
  errorScope?: string;
}

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  DialogContentProps
>(function DialogContent(props, ref) {
  const {
    children,
    portalled = true,
    portalRef,
    backdrop = true,
    backdropProps,
    positionerProps,
    withErrorBoundary = true,
    errorScope,
    ...rest
  } = props;
  const layer = useModalLayer({ kind: "dialog" });

  // Crash inside the dialog body should NOT close the dialog. Wrap the
  // children so a render error renders an inline error panel within the
  // dialog frame instead.
  const safeChildren = withErrorBoundary ? (
    <IsolatedErrorBoundary scope={errorScope}>{children}</IsolatedErrorBoundary>
  ) : (
    children
  );

  return (
    <Portal disabled={!portalled} container={portalRef}>
      {backdrop && (
        <ChakraDialog.Backdrop
          {...backdropProps}
          ref={pinLayerZIndex(
            layer.zIndex ? `calc(${layer.zIndex} - 1)` : undefined,
          )}
        />
      )}
      <ChakraDialog.Positioner
        {...positionerProps}
        ref={pinLayerZIndex(layer.zIndex)}
        data-layer="dialog"
        data-layer-depth={layer.depth}
      >
        <OverlayDepthContext.Provider value={layer.depth}>
          <ModalLayerContext.Provider value={true}>
            <ChakraDialog.Content ref={ref} {...rest} asChild={false}>
              {safeChildren}
            </ChakraDialog.Content>
          </ModalLayerContext.Provider>
        </OverlayDepthContext.Provider>
      </ChakraDialog.Positioner>
    </Portal>
  );
});

/** The header's close button: an icon button named "Close", inert while busy. */
export const DialogCloseTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraDialog.CloseTriggerProps
>(function DialogCloseTrigger(props, ref) {
  const busy = useDialogBusy();
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="var(--space-3)"
      insetEnd="var(--space-3)"
      {...props}
      asChild
    >
      <CloseButton size="sm" ref={ref} disabled={busy}>
        {props.children}
      </CloseButton>
    </ChakraDialog.CloseTrigger>
  );
});

export type DialogRootProps = Omit<ChakraDialog.RootProps, "size"> & {
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "5xl" | "6xl" | "cover" | "full";
  /**
   * The dialog is saving: Esc, a press outside and the close button are inert
   * and a close request is ignored, so the content stays until the work ends.
   */
  busy?: boolean;
};

export const DialogRoot = function DialogRoot({
  busy = false,
  onOpenChange,
  closeOnEscape,
  closeOnInteractOutside,
  finalFocusEl,
  onEscapeKeyDown,
  ...props
}: DialogRootProps) {
  const handleOpenChange = React.useCallback(
    (details: ChakraDialog.OpenChangeDetails) => {
      if (busy && !details.open) return;
      onOpenChange?.(details);
    },
    [busy, onOpenChange],
  );
  // Where focus goes back on close. Opened from a menu item, the element that
  // had focus is gone by the time the dialog closes, so the menu's button
  // (the row's "more" button) takes it instead.
  const returnTo = React.useRef<HTMLElement | null>(null);
  const wasOpen = React.useRef(false);
  if (props.open && !wasOpen.current) returnTo.current = openerForReturnFocus();
  wasOpen.current = !!props.open;
  const resolveFinalFocus = React.useCallback(
    () => finalFocusEl?.() ?? returnTo.current,
    [finalFocusEl],
  );
  return (
    <DialogBusyContext.Provider value={busy}>
      <ChakraDialog.Root
        trapFocus
        preventScroll={false}
        {...(props as ChakraDialog.RootProps)}
        // Only set when there is something to say: an explicit `undefined`
        // would replace Zag's own defaults.
        {...(busy || closeOnEscape !== undefined
          ? { closeOnEscape: busy ? false : closeOnEscape }
          : {})}
        {...(busy || closeOnInteractOutside !== undefined
          ? { closeOnInteractOutside: busy ? false : closeOnInteractOutside }
          : {})}
        finalFocusEl={resolveFinalFocus}
        onOpenChange={handleOpenChange}
        // Records the press that closes this dialog, so the key stops there
        // (`useOverlayZIndex`, "Esc: one stack, one rule").
        onEscapeKeyDown={escapeKeyDownThatMarksClose({
          closeOnEscape: busy ? false : closeOnEscape,
          onEscapeKeyDown,
        })}
      />
    </DialogBusyContext.Provider>
  );
};

interface DialogBodyProps extends ChakraDialog.BodyProps {
  /**
   * What went wrong, shown as an error banner at the top of the body. The
   * dialog stays open and keeps the person's input.
   */
  error?: React.ReactNode;
}

export const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
  function DialogBody({ error, children, ...rest }, ref) {
    return (
      <ChakraDialog.Body ref={ref} {...rest}>
        {error ? <DialogErrorBanner>{error}</DialogErrorBanner> : null}
        {children}
      </ChakraDialog.Body>
    );
  },
);

/**
 * The error banner at the top of a dialog's body: the err tint, a 1px border,
 * an icon and the message. Announced when it appears.
 */
export function DialogErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <Alert.Root
      status="error"
      role="alert"
      marginBottom="var(--space-3)"
      background="var(--color-err-soft)"
      color="var(--color-err-soft-fg)"
      border="1px solid"
      borderColor="var(--color-err)"
      borderRadius="var(--radius-3)"
      paddingBlock="var(--space-2)"
      paddingInline="var(--space-3)"
      gap="var(--space-2)"
      fontSize="var(--text-13)"
      data-dialog-error=""
    >
      <Alert.Indicator
        color="var(--color-err)"
        width="var(--icon-sm)"
        height="var(--icon-sm)"
      />
      <Alert.Content>
        <Alert.Description>{children}</Alert.Description>
      </Alert.Content>
    </Alert.Root>
  );
}

export const DialogFooter = ChakraDialog.Footer;
export const DialogHeader = ChakraDialog.Header;
export const DialogTitle = ChakraDialog.Title;
export const DialogDescription = ChakraDialog.Description;
export const DialogTrigger = ChakraDialog.Trigger;
export const DialogActionTrigger = ChakraDialog.ActionTrigger;

export const Dialog = {
  Root: DialogRoot,
  Content: DialogContent,
  CloseTrigger: DialogCloseTrigger,
  Footer: DialogFooter,
  Header: DialogHeader,
  Body: DialogBody,
  ErrorBanner: DialogErrorBanner,
  // `Backdrop` is intentionally NOT exported. `Dialog.Content` already
  // renders the one backdrop (the scrim); mounting a second one stacks two
  // scrims and doubles the dimming.
  Title: DialogTitle,
  Description: DialogDescription,
  Trigger: DialogTrigger,
  ActionTrigger: DialogActionTrigger,
};
